/* HelpDialog (iter-35) — in-app The Loom manual.
 *
 * Fetches /api/help/manual.json (single source of truth shared with the
 * PDF generator) and renders a left-side TOC + main reading pane.
 * Includes a "Download PDF" button.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import * as Icons from 'lucide-react';
import { BookOpen, Download, Search, Lightbulb, ListChecks, Loader2, Clock } from 'lucide-react';
import { helpApi } from '@/lib/api';

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = Date.now() - d.getTime();
    const min = Math.round(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hrs = Math.round(min / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

function IconFor({ name, className = 'h-4 w-4' }) {
  const Comp = Icons[name] || BookOpen;
  return <Comp className={className} />;
}

function Block({ block }) {
  if (block.kind === 'para') {
    return <p className="text-[13.5px] leading-relaxed text-foreground/90" dangerouslySetInnerHTML={{ __html: mdToHtml(block.text) }} />;
  }
  if (block.kind === 'list') {
    return (
      <ul className="ml-4 list-disc space-y-1 text-[13.5px] leading-relaxed text-foreground/90">
        {block.items.map((x, i) => (
          // List items are stable strings → use the content as part of the key.
          <li key={`${i}-${String(x).slice(0, 20)}`} dangerouslySetInnerHTML={{ __html: mdToHtml(x) }} />
        ))}
      </ul>
    );
  }
  if (block.kind === 'step') {
    return (
      <div className="flex gap-3" data-testid={`help-step-${block.n}`}>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[12px] font-semibold text-primary">{block.n}</div>
        <div className="text-[13.5px] leading-relaxed text-foreground/90 pt-0.5" dangerouslySetInnerHTML={{ __html: mdToHtml(block.text) }} />
      </div>
    );
  }
  if (block.kind === 'tip') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 flex gap-2" data-testid="help-tip">
        <Lightbulb className="h-4 w-4 shrink-0 mt-0.5" />
        <div dangerouslySetInnerHTML={{ __html: mdToHtml(block.text) }} />
      </div>
    );
  }
  if (block.kind === 'code') {
    return <pre className="rounded-sm bg-muted px-2 py-1 font-mono text-[12px]">{block.text}</pre>;
  }
  return null;
}

function mdToHtml(text) {
  // Minimal markdown: **bold** and `inline code`.
  // Defense in depth (iter-50 code-audit follow-up):
  //   1. HTML-escape the raw text BEFORE applying the markdown patterns —
  //      this means any `<script>` payload inside **bold** or `code` is
  //      already neutralised to `&lt;script&gt;` before we wrap it.
  //   2. DOMPurify.sanitize() on the final string strips any tag that
  //      isn't in our explicit allow-list (`b`, `code`) — so even if a
  //      future maintainer relaxes the escape step, the output stays safe.
  let s = String(text || '');
  s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  s = s.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  s = s.replace(/`(.+?)`/g, '<code class="rounded-sm bg-muted px-1 py-0.5 font-mono text-[11.5px]">$1</code>');
  return DOMPurify.sanitize(s, { ALLOWED_TAGS: ['b', 'code'], ALLOWED_ATTR: ['class'] });
}

export function HelpDialog({ open, onOpenChange }) {
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    helpApi.manual()
      .then((m) => { setManual(m); setActiveId(m.sections?.[0]?.id || ''); })
      .catch(() => setManual(null))
      .finally(() => setLoading(false));
  }, [open]);

  // iter55f85 — Track help section views + dwell time. Fires when
  // `activeId` changes (writer clicks a TOC row) and when the dialog
  // closes (final `_close` flush from analytics.js).
  useEffect(() => {
    if (!open || !activeId) return undefined;
    let cancelled = false;
    import('@/lib/analytics').then(({ trackHelpSectionOpen, trackHelpSectionClose }) => {
      if (cancelled) return;
      trackHelpSectionOpen(activeId);
      return () => { trackHelpSectionClose(); };
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [open, activeId]);
  useEffect(() => {
    if (open) return undefined;
    // Dialog closed — flush the current section's dwell timer.
    import('@/lib/analytics').then(({ trackHelpSectionClose }) => trackHelpSectionClose())
      .catch(() => { /* silent */ });
    return undefined;
  }, [open]);

  const visibleSections = useMemo(() => {
    if (!manual?.sections) return [];
    const q = query.trim().toLowerCase();
    if (!q) return manual.sections;
    return manual.sections.filter((s) => {
      if (s.title.toLowerCase().includes(q)) return true;
      if (s.summary.toLowerCase().includes(q)) return true;
      return (s.blocks || []).some((b) => JSON.stringify(b).toLowerCase().includes(q));
    });
  }, [manual, query]);

  const active = visibleSections.find((s) => s.id === activeId) || visibleSections[0];

  const downloadPdf = () => {
    window.open(helpApi.pdfUrl(), '_blank', 'noopener');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] p-0 overflow-hidden" data-testid="help-dialog">
        <DialogHeader className="border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" /> {manual?.title || 'The Loom · User Manual'}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between gap-3">
            <span>{manual?.subtitle || ''} <span className="ml-1 font-mono-stat text-[11px]">{manual?.version}</span></span>
            <Button size="sm" variant="outline" onClick={downloadPdf} className="gap-1.5" data-testid="help-download-pdf-btn">
              <Download className="h-3.5 w-3.5" /> Download PDF
            </Button>
          </DialogDescription>
        </DialogHeader>

        <HelpSubscribeBanner />

        {loading ? (
          <div className="flex h-72 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-12 h-[70vh]">
            {/* TOC sidebar */}
            <div className="col-span-4 sm:col-span-3 border-r border-border bg-panel-header/20 overflow-y-auto">
              <div className="sticky top-0 bg-panel-header/40 px-3 py-2 border-b border-border">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input className="h-8 pl-7 text-[12px]" placeholder="Search…"
                           value={query} onChange={e => setQuery(e.target.value)}
                           data-testid="help-search-input" />
                </div>
              </div>
              <nav className="py-1">
                {visibleSections.map((s) => (
                  <button key={s.id} type="button"
                           onClick={() => setActiveId(s.id)}
                           data-testid={`help-toc-${s.id}`}
                           className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-secondary ${active?.id === s.id ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground'}`}>
                    <IconFor name={s.icon} className="h-3.5 w-3.5" />
                    <span className="truncate">{s.title}</span>
                  </button>
                ))}
                {visibleSections.length === 0 && (
                  <div className="px-3 py-4 text-[12px] text-muted-foreground">No matches.</div>
                )}
              </nav>
            </div>
            {/* Content pane */}
            <div className="col-span-8 sm:col-span-9 overflow-y-auto px-6 py-5">
              {active ? (
                <article className="space-y-4" data-testid={`help-section-${active.id}`}>
                  <header>
                    <div className="flex items-center gap-2 text-primary">
                      <IconFor name={active.icon} className="h-5 w-5" />
                      <h2 className="text-[20px] font-semibold tracking-tight">{active.title}</h2>
                    </div>
                    <p className="mt-1 text-[12.5px] text-muted-foreground">{active.summary}</p>
                    {active.updated_at && (
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/80"
                          data-testid="help-section-updated-at">
                        <Clock className="h-3 w-3" /> Last updated {fmtRelative(active.updated_at)}
                      </p>
                    )}
                  </header>
                  <div className="space-y-3 pt-2">
                    {(active.blocks || []).map((b, i) => <Block key={`${b.kind}-${i}-${String(b.text || (b.items && b.items[0]) || '').slice(0, 24)}`} block={b} />)}
                  </div>
                  <div className="pt-4 border-t border-border text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <ListChecks className="h-3.5 w-3.5" /> End of section. Use the sidebar to jump elsewhere.
                  </div>
                </article>
              ) : (
                <div className="p-6 text-muted-foreground">Select a topic on the left to begin.</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



// iter-50m — Subscribe-banner nudge. Surfaces the help-update opt-in at
// the moment of highest intent (the user is reading the manual). One
// soft-dismiss = remembered forever via localStorage; opting in or
// hitting the X both stop the nudge from re-appearing on later visits.
function HelpSubscribeBanner() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const [phase, setPhase] = useState('loading'); // 'loading' | 'showing' | 'hidden' | 'busy' | 'done'

  useEffect(() => {
    let alive = true;
    try {
      if (localStorage.getItem('cb_help_nudge_dismissed') === '1') {
        setPhase('hidden');
        return undefined;
      }
    } catch (_e) { /* no-op */ }
    fetch(`${BACKEND}/api/help/subscribe`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!alive) return;
        if (!d) { setPhase('hidden'); return; }   // not signed in
        if (d.opt_in)  setPhase('hidden');         // already subscribed
        else           setPhase('showing');
      })
      .catch(() => { if (alive) setPhase('hidden'); });
    return () => { alive = false; };
  }, [BACKEND]);

  const dismiss = () => {
    try { localStorage.setItem('cb_help_nudge_dismissed', '1'); } catch (_e) { /* no-op */ }
    setPhase('hidden');
  };

  const subscribe = async () => {
    setPhase('busy');
    try {
      const r = await fetch(`${BACKEND}/api/help/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opt_in: true }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      try { localStorage.setItem('cb_help_nudge_dismissed', '1'); } catch (_e) { /* no-op */ }
      setPhase('done');
    } catch (_e) {
      setPhase('showing');
    }
  };

  if (phase === 'hidden' || phase === 'loading') return null;

  return (
    <div
      data-testid="help-subscribe-banner"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-300/50 bg-amber-50 px-5 py-2 text-[12px] text-amber-900"
    >
      {phase === 'done' ? (
        <span className="font-semibold">You&apos;re subscribed — thanks!</span>
      ) : (
        <>
          <div className="flex-1 min-w-[260px]">
            <span className="font-semibold">Get notified when the manual updates.</span>{' '}
            <span className="opacity-80">Occasional emails (≈ once per release) describing what changed and how it affects your workflow. One-click unsubscribe in every email.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={dismiss}
              data-testid="help-subscribe-dismiss"
              className="h-7 border-amber-400/60 bg-white/60 text-amber-900 hover:bg-white"
            >
              Not now
            </Button>
            <Button
              size="sm"
              onClick={subscribe}
              disabled={phase === 'busy'}
              data-testid="help-subscribe-confirm"
              className="h-7 gap-1.5 bg-amber-700 text-white hover:bg-amber-800"
            >
              {phase === 'busy' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Subscribe
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
